import { Box, Button, Slider, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { clamp } from '../../util/functions';
import type { FilterFieldConfig } from '../CatalogFilterModel/filterModel';
import {
  filtersPatchFromSliderValue,
  formatRangeValue,
  parseMonthInput,
  sliderValueFromFilters,
} from '../CatalogFilterModel/filterModel';
import * as styles from './CatalogFilterBar.styles';

interface RangeFilterProps {
  field: FilterFieldConfig;
  filters: Record<string, unknown> | undefined;
  /** Commit a multi-key patch; keys set to `undefined` are cleared. */
  onCommit: (patch: Record<string, string | number | undefined>) => void;
}

/**
 * Two-handle range slider plus min/max text inputs for a `range`
 * FilterFieldConfig. Works in slider-index space and commits through the shared
 * filterModel conversions. Year scale shows/accepts plain numbers; month scale
 * shows/accepts "Mon YYYY" labels. Typed values clamp into the track bounds and
 * revert on blur when unparseable. Uses theme `primary` (no color override).
 */
export const RangeFilter = ({ field, filters, onCommit }: RangeFilterProps) => {
  const min = field.min ?? 0;
  const max = field.max ?? 0;

  const format = (n: number): string =>
    field.scale === 'month' ? formatRangeValue(field, n, 'short') : String(n);

  const parse = (text: string): number | null => {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    if (field.scale === 'month') return parseMonthInput(field, trimmed);
    const num = Number(trimmed);
    return Number.isFinite(num) ? Math.round(num) : null;
  };

  const committed = sliderValueFromFilters(filters, field);
  const [value, setValue] = useState<[number, number]>(committed);
  const [minText, setMinText] = useState<string>(format(committed[0]));
  const [maxText, setMaxText] = useState<string>(format(committed[1]));
  const lastCommittedRef = useRef<string>(JSON.stringify(committed));

  // Re-sync when the committed value changes externally (Clear all, reopen).
  useEffect(() => {
    const next = sliderValueFromFilters(filters, field);
    const key = JSON.stringify(next);
    if (key !== lastCommittedRef.current) {
      lastCommittedRef.current = key;
      setValue(next);
      setMinText(format(next[0]));
      setMaxText(format(next[1]));
    }
  }, [filters, field]);

  const commit = (next: [number, number]) => {
    setValue(next);
    setMinText(format(next[0]));
    setMaxText(format(next[1]));
    // Skip the (page-resetting, refetch-triggering) commit when the range is
    // unchanged — e.g. focusing an input and blurring without editing, or
    // releasing the slider at the same spot. Keep lastCommittedRef in step so
    // the external re-sync effect stays a no-op on our own commits.
    const key = JSON.stringify(next);
    if (key === lastCommittedRef.current) return;
    lastCommittedRef.current = key;
    onCommit(filtersPatchFromSliderValue(next, field));
  };

  const handleMinBlur = () => {
    const parsed = parse(minText);
    if (parsed == null) {
      setMinText(format(value[0]));
      return;
    }
    const low = clamp(parsed, min, Math.min(value[1], max));
    commit([low, value[1]]);
  };

  const handleMaxBlur = () => {
    const parsed = parse(maxText);
    if (parsed == null) {
      setMaxText(format(value[1]));
      return;
    }
    const high = clamp(parsed, Math.max(value[0], min), max);
    commit([value[0], high]);
  };

  const hasSelection = value[0] !== min || value[1] !== max;

  return (
    <Box sx={styles.popoverContent}>
      <Box sx={styles.sliderBox}>
        <Slider
          value={value}
          min={min}
          max={max}
          step={field.step ?? 1}
          valueLabelDisplay="auto"
          valueLabelFormat={(n) => format(n)}
          getAriaLabel={() => field.label}
          onChange={(_, v) => {
            const next = v as [number, number];
            setValue(next);
            setMinText(format(next[0]));
            setMaxText(format(next[1]));
          }}
          onChangeCommitted={(_, v) => commit(v as [number, number])}
          disableSwap
        />
      </Box>
      <Box sx={styles.inputRow}>
        <TextField
          label="min"
          size="small"
          value={minText}
          onChange={(e) => setMinText(e.target.value)}
          onBlur={handleMinBlur}
          sx={{ flex: 1 }}
        />
        <TextField
          label="max"
          size="small"
          value={maxText}
          onChange={(e) => setMaxText(e.target.value)}
          onBlur={handleMaxBlur}
          sx={{ flex: 1 }}
        />
      </Box>
      <Button
        variant="text"
        size="small"
        disabled={!hasSelection}
        onClick={() => commit([min, max])}
        sx={styles.clearButton}
      >
        Clear
      </Button>
    </Box>
  );
};
